type QuillOp = {
  insert: string | { image?: string } | FirstLineIndentInsert;
  attributes?: Record<string, any>;
};

type FirstLineIndentInsert = {
  "firstline-indent": { width?: string } | string;
};

type DeltaInput =
  | { ops: QuillOp[] }
  | QuillOp[]
  | {
      insert: string | { image?: string } | FirstLineIndentInsert;
      attributes?: Record<string, any>;
    }[];

const escapeHtml = (s: string) =>
  s
    .replaceAll(/&/g, "&amp;")
    .replaceAll(/</g, "&lt;")
    .replaceAll(/>/g, "&gt;")
    .replaceAll(/"/g, "&quot;")
    .replaceAll(/'/g, "&#39;");

const applyInline = (text: string, attrs?: Record<string, any>) => {
  if (!text) return "";
  let html = escapeHtml(text);

  if (!attrs) return html;

  if (attrs.link) {
    const href = String(attrs.link);
    html = `<a href="${escapeHtml(
      href
    )}" target="_blank" rel="noopener noreferrer">${html}</a>`;
  }
  if (attrs.bold) html = `<strong>${html}</strong>`;
  if (attrs.italic) html = `<em>${html}</em>`;
  if (attrs.underline) html = `<u>${html}</u>`;
  if (attrs.strike) html = `<s>${html}</s>`;
  if (attrs.code) html = `<code>${html}</code>`;
  if (attrs.color)
    html = `<span style="color:${escapeHtml(
      String(attrs.color)
    )}">${html}</span>`;
  if (attrs.background)
    html = `<span style="background:${escapeHtml(
      String(attrs.background)
    )}">${html}</span>`;
  if (attrs.script === "sub") html = `<sub>${html}</sub>`;
  if (attrs.script === "super") html = `<sup>${html}</sup>`;

  return html;
};

const blockTagFromHeader = (lvl: number) => {
  const n = Math.min(6, Math.max(1, lvl || 1));
  return `h${n}`;
};  

/**
 * Convert a Quill Delta to HTML.
 * Supports: bold/italic/underline/strike/code/link/color/background/script, headers,
 * lists (ordered/bullet) with indent nesting, blockquote, code-block (grouped),
 * align (left/center/right/justify), and images.
 */
export const useConvertQuillDeltaToHtml = () => {
  const toHtml = (deltaInput: any): string => {
    const ops: QuillOp[] = Array.isArray(deltaInput)
      ? (deltaInput as QuillOp[])
      : "ops" in (deltaInput as any)
      ? (deltaInput as any).ops
      : [];

    let html = "";

    // list nesting state
    type ListFrame = { type: "ul" | "ol"; indent: number };
    const listStack: ListFrame[] = [];

    const openList = (type: "ul" | "ol", indent: number) => {
      listStack.push({ type, indent });
      html += `<${type} style='font-family: 'Times New Roman', serif;'>`;
    };
    const closeList = () => {
      const frame = listStack.pop();
      if (frame) html += `</${frame.type}>`;
    };
    const closeAllLists = () => {
      while (listStack.length) closeList();
    };

    // code-block grouping state
    let inCodeBlock = false;
    let codeBuffer = "";

    const flushCodeBlock = () => {
      if (inCodeBlock) {
        html += `<pre><code>${escapeHtml(
          codeBuffer.replace(/\n$/, "")
        )}</code></pre>`;
        inCodeBlock = false;
        codeBuffer = "";
      }
    };

    // helper to wrap a line into its block container
    const renderLine = (
      inlineHtml: string,
      blockAttrs?: Record<string, any>
    ) => {
      // lists handled outside via caller

      const styleProps: { [key: string]: string } = {};

      if (blockAttrs?.align) {
        styleProps["text-align"] = blockAttrs.align;
      }

      if (blockAttrs?.lineheight) {
        styleProps["line-height"] = blockAttrs.lineheight;
      }

      if (blockAttrs?.indent) {
        styleProps["margin-left"] = blockAttrs.indent * 3 + "em";
      }

      if (blockAttrs?.leftIndent) {
        styleProps["margin-left"] = blockAttrs.leftIndent;
      }
      if (blockAttrs?.firstline) {
        styleProps["text-indent"] = blockAttrs.firstline;
      }

      const styleString = useConvertStylePropsToString(styleProps);

      if (blockAttrs?.["code-block"]) {
        // handled by code-block aggregator
        return { handled: "code" as const, html: inlineHtml, styleString };
      }

      if (blockAttrs?.blockquote) {
        return {
          handled: "block" as const,
          html: `<blockquote${styleString}>${
            inlineHtml || "<br>"
          }</blockquote>`,
        };
      }

      if (blockAttrs?.header) {
        const tag = blockTagFromHeader(Number(blockAttrs.header));
        return {
          handled: "block" as const,
          html: `<${tag}${styleString}>${inlineHtml || ""}</${tag}>`,
        };
      }

      // default paragraph
      return {
        handled: "block" as const,
        html: `<p${styleString}>${inlineHtml || "<br>"}</p>`,
      };
    };

    // process ops
    let currentLineParts: string[] = [];
    let currentInlineAttrs: Record<string, any> | undefined;

    const flushLine = (blockAttrs?: Record<string, any>) => {
      const lineHtml = currentLineParts.join("") || "";
      currentLineParts = [];
      currentInlineAttrs = undefined;

      // list item?
      const listType = blockAttrs?.list as "ordered" | "bullet" | undefined;
      if (listType) {
        // close code block if we were in it
        flushCodeBlock();

        const tag = listType === "ordered" ? "ol" : "ul";
        const indent = Number(blockAttrs?.indent || 0);

        // adjust list stack to desired depth/type
        // close until we match depth
        while (
          listStack.length &&
          listStack[listStack.length - 1].indent > indent
        ) {
          closeList();
        }
        // same depth but different type -> close then open
        if (
          listStack.length &&
          listStack[listStack.length - 1].indent === indent &&
          listStack[listStack.length - 1].type !== tag
        ) {
          closeList();
        }
        // open deeper levels as needed
        while (
          !listStack.length ||
          listStack[listStack.length - 1].indent < indent
        ) {
          openList(tag, (listStack[listStack.length - 1]?.indent ?? -1) + 1);
        }
        // open current level if empty
        if (
          !listStack.length ||
          listStack[listStack.length - 1].indent !== indent ||
          listStack[listStack.length - 1].type !== tag
        ) {
          openList(tag, indent);
        }

        const align = blockAttrs?.align
          ? ` style="text-align:${escapeHtml(String(blockAttrs.align))}"`
          : "";
        html += `<li${align}>${lineHtml || "<br>"}</li>`;
        return;
      }

      // non-list line: close lists if any
      if (listStack.length) closeAllLists();

      // code-block aggregation
      const rendered = renderLine(lineHtml, blockAttrs);
      if (rendered.handled === "code") {
        // starting or continuing code block
        if (!inCodeBlock) inCodeBlock = true;
        codeBuffer += (lineHtml || "") + "\n";
      } else {
        // any other block should flush pending code
        flushCodeBlock();
        html += rendered.html;
      }
    };

    for (const op of ops) {
      // image embed
      if (
        typeof op.insert === "object" &&
        "image" in op.insert &&
        op.insert.image
      ) {
        // break line if we had content
        if (currentLineParts.length) {
          // treat as paragraph line break
          flushLine({});
        }
        // images are block-level by default
        flushCodeBlock();
        // close lists if any
        if (listStack.length) closeAllLists();

        const src = escapeHtml(String(op.insert.image));
        const align = op.attributes?.align
          ? ` style="text-align:${escapeHtml(String(op.attributes.align))}`
          : "";
        html += `<p${align}><img src="${src}" alt="" /></p>`;
        continue;
      }

      // custom firstline-indent embed
      if (typeof op.insert === "object" && "firstline-indent" in op.insert) {
        const val = op.insert["firstline-indent"];
        const width = typeof val === "string" ? val : val?.width || "2em";
        // render as an inline-block spacer at the start of the line
        currentLineParts.push(
          `<span style="display:inline-block;width:${escapeHtml(
            String(width)
          )}"></span>`
        );
        // do not fall through to text handling
        continue;
      }

      // text insert
      const text = String(op.insert ?? "");

      if (text.length === 0) continue;

      const parts = text.split("\n");
      for (let i = 0; i < parts.length; i++) {
        const chunk = parts[i];

        if (chunk) {
          currentLineParts.push(applyInline(chunk, op.attributes));
          currentInlineAttrs = op.attributes;
        }
        if (i < parts.length - 1) {
          // newline encountered – flush the line with block attrs (on the newline)
          flushLine(op.attributes);
        }
      }
    }

    // flush trailing line if any content without newline
    if (currentLineParts.length) {
      flushLine({});
    }

    // close any pending structures
    flushCodeBlock();
    if (listStack.length) closeAllLists();

    return html;
  };

  return toHtml;
};
